import { useToast } from '../../platform/feedback/ToastProvider'
import { ConfirmDialog } from '../../platform/primitives/Dialog'
import { useTagCount } from '../transactions/useTransactions'
import { TagChangeError, useDeleteTag } from './useTagMutations'

const plural = (n: number) => `${n} transaction${n === 1 ? '' : 's'}`

/**
 * The count is the row's own, already cached, so this makes no new request. It excludes `is_removed`
 * rows the delete does touch, which is why the toast says what the SERVER reports, not this number.
 */
export function DeleteTagDialog({ name, onClose }: { name: string; onClose: () => void }) {
  const remove = useDeleteTag()
  const toast = useToast()
  const count = useTagCount(name, true)
  const scope = count.status === 'success' && count.count !== undefined ? plural(count.count) : 'every transaction that has it'

  return (
    <ConfirmDialog
      open
      mode="delete_tag"
      title={`Delete “${name}”?`}
      body={`“${name}” comes off ${scope}. The transactions stay as they are. Auto-rules that add “${name}” aren't changed and will keep adding it. This can’t be undone.`}
      confirmLabel="Delete tag"
      pendingLabel="Deleting…"
      destructive
      onCancel={onClose}
      errorFor={(thrown) =>
        thrown instanceof TagChangeError && thrown.status === 404
          ? 'That tag is no longer on any of your transactions. The list has been refreshed.'
          : "The tag wasn't deleted, and nothing has changed. Try again."
      }
      onConfirm={async () => {
        const result = await remove.mutateAsync(name)
        toast.show({ kind: 'success', message: `Removed “${name}” from ${plural(result.updated)}` })
        onClose()
      }}
    />
  )
}
